import React, { useState } from "react";
import { elakayAutoText } from "../../pageInside/dataElakay";


/* ── Design tokens ────────────────────────────────────────────────── */
const T = {
  bg:        "#f8fafc",
  white:     "#ffffff",
  border:    "#e2e8f0",
  borderFocus:"#007399",
  primary:   "#007399",
  primaryDk: "#005a73",
  primaryLt: "#e0f4fa",
  green:     "#10b981",
  greenLt:   "#f0fdf4",
  greenBd:   "#a7f3d0",
  red:       "#ef4444",
  redLt:     "#fef2f2",
  redBd:     "#fca5a5",
  amber:     "#f59e0b",
  txt1:      "#0f172a",
  txt2:      "#475569",
  txt3:      "#94a3b8",
  font:      "'Inter','Segoe UI',system-ui,sans-serif",
  mono:      "'JetBrains Mono','Fira Code','Cascadia Code',monospace",
  radius:    10,
  radiusSm:  6,
};

/* ── Primitives ───────────────────────────────────────────────────── */
const fieldBorder = (s) => ({
  valid:   `1.5px solid ${T.greenBd}`,
  invalid: `1.5px solid ${T.redBd}`,
  neutral: `1.5px solid ${T.border}`,
}[s] || `1.5px solid ${T.border}`);

const fieldBg = (s) => ({
  valid:   T.greenLt,
  invalid: T.redLt,
  neutral: T.white,
}[s] || T.white);

/* ── Toggle switch ───────────────────────────────────────────────── */
const Toggle = ({ checked, onChange, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <button
      type="button"
      onClick={onChange}
      style={{
        position: "relative", width: 40, height: 22, padding: 0,
        border: "none", borderRadius: 11,
        background: checked ? T.primary : "#cbd5e1",
        cursor: "pointer", transition: "background .2s", flexShrink: 0,
      }}
      aria-checked={checked}
      role="switch"
    >
      <span style={{
        position: "absolute", top: 3,
        left: checked ? 19 : 3,
        width: 16, height: 16,
        background: T.white,
        borderRadius: "50%",
        transition: "left .2s",
        display: "block",
        boxShadow: "0 1px 3px rgba(0,0,0,.2)",
      }} />
    </button>
    {label && (
      <span style={{ fontSize: 12, color: T.txt3, fontWeight: 600, fontFamily: T.font }}>
        {label}
      </span>
    )}
  </div>
);

/* ── Status dot ──────────────────────────────────────────────────── */
const StatusDot = ({ status }) => {
  const color = { saved: T.green, saving: T.amber, error: T.red }[status] || T.txt3;
  const label = { saved: "Saved", saving: "Saving…", error: elakayAutoText("h_7f2f6a15cf8da2", "Error") }[status] || "Ready";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{
        width: 7, height: 7, borderRadius: "50%",
        background: color, display: "block",
        boxShadow: `0 0 0 2px ${color}33`,
      }} />
      <span style={{ fontSize: 11, color: T.txt3, fontFamily: T.font, fontWeight: 500 }}>
        {label}
      </span>
    </div>
  );
};

/* ── Main component ──────────────────────────────────────────────── */
const FormPageLang = (props) => {
  const {
    infoSource,
    editAdd, setEditAdd,
    menu, nameObj, objet, setObejet,
    infoElakay, edit_add, token,
    AlllanguageSelect, languageUsertra, languageTradictor,
    updateLanguageElakay, buttonAddLanguage, setError
  } = props;

  const txt = (key, fallback = '') => {
    const val = infoSource?.laguangeElakay?.(
      "frontElakayHardcodedText", infoSource.languageAPI
    )?.languageFormPageLang?.[key];
    return typeof val === 'string' ? val : fallback;
  };

  /* ── state ── */
  const [errorIn, setErrorIn]   = useState('');
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error

  /* ── validation helpers ── */
  const checkProdKeyExit = (ad, ld) => {
    const ent  = Object.keys(ad ?? {});
    const lent = Object.keys(ld ?? {});
    const apiEvery = ent.every((key) => ad[key] != null);
    return apiEvery && ent.length === lent.length;
  };

  const checkObjEmpty   = (a) => JSON.stringify(a) === '{}';
  const checkProductTrue = (ad) =>
    Object.values(ad).every((e) => e !== '');

  const checkUndefName = (a, b) => a && Object.keys(a).includes(b);

  const inputOK = (a, b, c) => {
    if (c == null)       return { v: b,    s: 'neutral' };
    if (checkUndefName(c, a)) return { v: c[a], s: 'valid'   };
    return { v: "", s: 'invalid' };
  };

  const checkValue = (val) =>
    languageTradictor[nameObj] != null ? languageTradictor[nameObj] : val;

  /* ── handlers ── */
  const clickEditAdd = () => {
    setEditAdd(!editAdd);
    if (!edit_add && !checkProdKeyExit(languageTradictor[nameObj], infoElakay)) {
      setObejet(editAdd ? languageTradictor[nameObj] : menu);
    } else {
      setErrorIn(editAdd ? "No data to edit" : "");
      setObejet(infoElakay);
    }
  };

  const handleChange = (name) => (event) => {
    setObejet({ ...objet, [name]: event.target.value });
  };

  const buttonAdd = () => {
    setSaveStatus('saving');
    updateLanguageElakay(languageTradictor._id, token, { [nameObj]: objet }).then((data) => {
      if (data.error) {
        setError(data.error);
        setErrorIn(data.error);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      } else {
        AlllanguageSelect();
        languageUsertra();
        setEditAdd(true);
        setObejet(menu);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2500);
      }
    });
  };

  /* ── field renderer ── */
  const entries = Object.entries(infoElakay || {});

  const InputField = (fieldKey, labelText, i, refObj) => {
    if (!labelText) return null;
    const { s } = inputOK(fieldKey, labelText, checkValue(refObj));
    const isLong  = labelText.length > 90;
    const maxLen  = isLong ? 800 : 300;
    const currLen = (objet?.[fieldKey] || '').length;
    const nearMax = currLen > maxLen * 0.85;

    const sharedStyle = {
      width: "100%",
      padding: "9px 12px",
      boxSizing: "border-box",
      border: fieldBorder(s),
      borderRadius: T.radiusSm,
      fontSize: 13,
      color: T.txt1,
      fontFamily: isLong ? T.mono : T.font,
      background: fieldBg(s),
      outline: "none",
      transition: "border-color .15s, background .15s",
      lineHeight: 1.6,
    };

    return (
      <div key={i} style={{ marginBottom: 14 }}>
        {/* Label row */}
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "flex-end", marginBottom: 5,
        }}>
          <label style={{
            fontSize: 11, fontWeight: 700,
            color: T.txt3, textTransform: "uppercase",
            letterSpacing: 0.7, fontFamily: T.font,
            lineHeight: 1,
          }}>
            {labelText.length > 56 ? labelText.slice(0, 54) + "…" : labelText}
          </label>
          <span style={{
            fontSize: 10, fontWeight: 600,
            color: nearMax ? T.red : T.txt3,
            fontFamily: T.mono, lineHeight: 1,
            transition: "color .2s",
          }}>
            {currLen}/{maxLen}
          </span>
        </div>

        {/* Input or textarea */}
        {isLong
          ? (
            <textarea
              onChange={handleChange(fieldKey)}
              value={objet?.[fieldKey] ?? ''}
              rows={3}
              maxLength={maxLen}
              style={{ ...sharedStyle, resize: "vertical" }}
            />
          ) : (
            <input
              type="text"
              onChange={handleChange(fieldKey)}
              value={objet?.[fieldKey] ?? ''}
              maxLength={maxLen}
              style={sharedStyle}
            />
          )
        }

        {/* Validation indicator */}
        {s === 'valid' && (
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.green} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span style={{ fontSize: 10, color: T.green, fontFamily: T.font, fontWeight: 600 }}>
              {txt("tOk", "Translation exists")}
            </span>
          </div>
        )}
      </div>
    );
  };

  /* ── render ── */
  return (
    <div style={{ fontFamily: T.font, borderRadius: T.radius, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>

      {/* ── Panel header ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 20px",
        background: "#0f172a",
        borderBottom: "1px solid #1e293b",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 7,
            background: `${T.primary}22`,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.primary} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", lineHeight: 1.2 }}>
              {txt("t002", "Translation Editor")}
            </div>
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 1 }}>
              {entries.length} {txt("tFields", "fields")}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <StatusDot status={saveStatus} />
          <Toggle
            checked={editAdd}
            onChange={clickEditAdd}
            label={txt("t001", "edit / add")}
          />
        </div>
      </div>

      {/* ── Error banner ── */}
      {errorIn && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 20px",
          background: T.redLt,
          borderBottom: `1px solid ${T.redBd}`,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.red} strokeWidth="2.5" strokeLinecap="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span style={{ fontSize: 12, color: "#991b1b", fontWeight: 500 }}>{errorIn}</span>
          <button
            type="button"
            onClick={() => setErrorIn('')}
            style={{
              marginLeft: "auto", background: "none", border: "none",
              cursor: "pointer", color: "#991b1b", fontSize: 16, lineHeight: 1,
            }}
            aria-label={elakayAutoText("h_70afe9eff3f288", "Dismiss")}
          >
            ×
          </button>
        </div>
      )}

      {/* ── Form body ── */}
      <div style={{
        background: T.bg,
        borderLeft: `1px solid ${T.border}`,
        borderRight: `1px solid ${T.border}`,
      }}>
        <div
          style={{ padding: "20px 20px 0" }}
          onBlur={() => setErrorIn("")}
        >
          {/* Field count info */}
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "8px 12px",
            background: T.primaryLt,
            borderRadius: T.radiusSm,
            marginBottom: 16,
            border: `1px solid #b3e4f3`,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.primaryDk} strokeWidth="2.5" strokeLinecap="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="8" />
              <line x1="12" y1="12" x2="12" y2="16" />
            </svg>
            <span style={{ fontSize: 11, color: T.primaryDk, fontWeight: 600 }}>
              {editAdd
                ? txt("tModeEdit", "Editing existing translation")
                : txt("tModeAdd",  "Adding new translation")}
            </span>
          </div>

          <form onSubmit={(e) => e.preventDefault()}>
            {entries.map(([key, val], i) =>
              InputField(key, val, i, checkValue({}))
            )}
          </form>
        </div>
      </div>

      {/* ── Action bar ── */}
      <div style={{
        display: "flex", justifyContent: "flex-end", alignItems: "center",
        gap: 10,
        padding: "14px 20px",
        background: T.white,
        borderTop: `1px solid ${T.border}`,
        borderLeft: `1px solid ${T.border}`,
        borderRight: `1px solid ${T.border}`,
        borderBottom: `1px solid ${T.border}`,
        borderRadius: `0 0 ${T.radius}px ${T.radius}px`,
      }}>
        {saveStatus === 'saved' && (
          <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: T.green, fontWeight: 600 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.green} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {txt("tSaved", "Saved!")}
          </span>
        )}

        {buttonAddLanguage(
          buttonAdd,
          checkProductTrue(objet ?? {}) && !checkObjEmpty(languageTradictor)
        )}
      </div>
    </div>
  );
};

export default FormPageLang;
